import { computed, inject, onMounted, ref, watch, type Ref } from 'vue'
import {
  readMallUiScope,
  type MallUiScope
} from '@/layout/standalone/mallMenuScopes'
import {
  ensureDefaultSelfOperatedShops,
  pickDefaultSelfOperatedShopId,
  type MallSelfOperatedShop
} from '@/utils/mallSelfOperatedShop'

/** 列表 / 统计查询上的店铺过滤字段 */
export type MallSelfShopQueryFilter = {
  shopId?: number
  shopIds?: number[]
}

export type MallSelfShopContext = {
  /** 当前是否自营视角 */
  selfScope: boolean
  /** 自营店集合 S */
  shops: MallSelfOperatedShop[]
  /** |S|=1 时的本店 id */
  singleShopId?: number
  /** |S|>1 异常多店 */
  multiSelfAbnormal: boolean
  /** |S|=0（ensure 仍失败） */
  emptySelfShops: boolean
  /** 订单/售后顶栏提示 */
  listTip?: string
  /** 概况交易卡口径：本店 / 自营汇总；非自营为空 */
  tradeScopeHint: string
  /** 概况会员类口径 */
  memberScopeHint: string
  /** 加载中 */
  loading: boolean
}

/**
 * 自营视角「本店」上下文（概况 / 订单 / 售后共用）。
 * 规则见：商城自营视角本店收敛-订单统计-规划-2026-08-17.md
 */
export function useMallSelfShopContext(options?: { autoLoad?: boolean }) {
  const autoLoad = options?.autoLoad !== false
  const injectedMallScope = inject<Ref<MallUiScope> | undefined>('mallUiScope', undefined)
  const shops = ref<MallSelfOperatedShop[]>([])
  const loading = ref(false)

  const resolveSelfScope = () =>
    (injectedMallScope?.value ?? readMallUiScope()) === 'self'

  const refresh = async () => {
    loading.value = true
    try {
      if (!resolveSelfScope()) {
        shops.value = []
        return
      }
      shops.value = await ensureDefaultSelfOperatedShops()
    } finally {
      loading.value = false
    }
  }

  const context = computed<MallSelfShopContext>(() => {
    const selfScope = resolveSelfScope()
    if (!selfScope) {
      return {
        selfScope: false,
        shops: [],
        multiSelfAbnormal: false,
        emptySelfShops: false,
        tradeScopeHint: '',
        memberScopeHint: '',
        loading: loading.value
      }
    }
    const list = shops.value
    const singleShopId = pickDefaultSelfOperatedShopId(list)
    const multiSelfAbnormal = list.length > 1
    const emptySelfShops = list.length === 0
    let listTip: string | undefined
    let tradeScopeHint = '本店'
    if (emptySelfShops) {
      listTip = '暂无自营店，无法按本店收敛；请确认有建店权限或联系管理员'
      tradeScopeHint = ''
    } else if (multiSelfAbnormal) {
      listTip =
        '存在多家自营店（非标准自营形态），当前为自营店汇总；请到平台视角整理'
      tradeScopeHint = '自营汇总'
    } else if (singleShopId && list[0]) {
      listTip = `已按本店（${list[0].name}）查看`
      tradeScopeHint = '本店'
    }
    return {
      selfScope: true,
      shops: list,
      singleShopId,
      multiSelfAbnormal,
      emptySelfShops,
      listTip,
      tradeScopeHint,
      memberScopeHint: '租户汇总',
      loading: loading.value
    }
  })

  /**
   * 生成查询过滤：单店 → shopId；多店汇总 → shopIds；空店 → shopId=-1 逼空结果。
   * @param selectedShopId 用户在筛选项中手动选中的店（须属于 S）
   */
  const buildShopFilter = (selectedShopId?: number | null): MallSelfShopQueryFilter => {
    const ctx = context.value
    if (!ctx.selfScope) {
      return {}
    }
    if (ctx.emptySelfShops) {
      return { shopId: -1 }
    }
    if (
      selectedShopId != null &&
      selectedShopId > 0 &&
      ctx.shops.some((s) => s.id === selectedShopId)
    ) {
      return { shopId: selectedShopId }
    }
    if (ctx.singleShopId) {
      return { shopId: ctx.singleShopId }
    }
    if (ctx.multiSelfAbnormal) {
      return { shopIds: ctx.shops.map((s) => s.id) }
    }
    return { shopId: -1 }
  }

  /** 把本店过滤合并进 query 对象（会删掉不应带的字段） */
  const applyShopFilterToQuery = <T extends Record<string, any>>(
    query: T,
    selectedShopId?: number | null
  ): T => {
    const filter = buildShopFilter(selectedShopId)
    delete query.shopId
    delete query.shopIds
    if (filter.shopId != null) {
      query.shopId = filter.shopId
    }
    if (filter.shopIds != null && filter.shopIds.length > 0) {
      query.shopIds = filter.shopIds
    }
    return query
  }

  if (autoLoad) {
    onMounted(() => {
      void refresh()
    })
  }

  if (injectedMallScope) {
    watch(
      injectedMallScope,
      () => {
        void refresh()
      },
      { flush: 'post' }
    )
  }

  return {
    context,
    shops,
    loading,
    refresh,
    buildShopFilter,
    applyShopFilterToQuery,
    resolveSelfScope
  }
}
